import moment from 'moment'
import { isIso8601Duration } from '../validator/is-iso-8601-duration'
import { TIME_UNITS } from '../constants/time-units'

/**
 * Casts a signed integer string to a signed integer, so moment will process it correctly, which would otherwise
 * return '0'. RegEx will check if a string starts with an optional dash, followed by any number of digits.
 *
 * @private
 * @param {String} duration - A string that will be checked for including a valid signed integer value.
 * @return {boolean} Whether the provided argument matches a signed integer.
 */
function isStringRepresentationOfSignedInteger(duration) {
    return typeof duration === 'string' && /^(?:0|(-?)[1-9](\d+)?)$/.test(duration)
}

/**
 * Uses moment.duration().as(timeUnit) to convert a duration provided either as ISO 8601 duration string or an integer
 * (string) in milliseconds into a numeric duration value of signed integer. It will be converted to the desired time
 * unit (rounded to full units).
 *
 * @param {String|number} duration - A string representing a (ISO 8601) duration or a (integer) string of milliseconds.
 * @param {String} [targetTimeUnit='minutes'] - The relative time unit the result will be converted to.
 * @returns {null|number} A signed integer that represents a converted duration for the provided time unit.
 */
const toNumericDurationValue = (duration, targetTimeUnit = 'minutes') => {
    duration = isStringRepresentationOfSignedInteger(duration) ? parseInt(duration) : duration

    const isValidTimeUnit = Object.values(TIME_UNITS).includes(targetTimeUnit)
    const isValidDuration = isIso8601Duration(duration) || Number.isInteger(duration)
    const momentDuration = moment.duration(duration)

    if (!isValidTimeUnit || !isValidDuration || isNaN(momentDuration.as(targetTimeUnit))) {
        return null
    }

    const numericDurationValue = momentDuration.as(targetTimeUnit)

    return numericDurationValue >= 0 ? Math.floor(numericDurationValue) : Math.ceil(numericDurationValue)
}

export { toNumericDurationValue }
